import api from './api';
import { CrimeRequest, CrimeResult, GameState } from '../types';
import { monitorApiCall } from '../utils/performance';

// Simple in-memory cache
const cache = new Map<string, { data: any; timestamp: number }>();

const CACHE_DURATION = 30000; // 30 seconds

const getCachedData = (key: string) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

const clearCache = (key?: string) => {
  if (key) {
    cache.delete(key);
  } else {
    cache.clear();
  }
};

export const gameService = {
  async commitCrime(data: CrimeRequest): Promise<CrimeResult> {
    const response = await monitorApiCall(
      () => api.post('/game/crime', data),
      '/game/crime'
    );
    // Clear user stats cache after crime to ensure fresh data
    clearCache('user-stats');
    console.log('🧹 Cleared user-stats cache after crime');
    return response.data;
  },

  async getGameState(): Promise<GameState> {
    const cacheKey = 'game-state';
    const cached = getCachedData(cacheKey);
    
    if (cached) {
      return cached;
    }
    
    const response = await monitorApiCall(
      () => api.get('/game/state'),
      '/game/state'
    );
    setCachedData(cacheKey, response.data);
    return response.data;
  },

  async getUserStats(): Promise<{ user: any; rankName: string; nextRank: any }> {
    const cacheKey = 'user-stats';
    const cached = getCachedData(cacheKey);
    
    if (cached) {
      return cached;
    }
    
    const response = await monitorApiCall(
      () => api.get('/game/stats'),
      '/game/stats'
    );
    setCachedData(cacheKey, response.data);
    return response.data;
  },

  // Utility function to clear cache when needed
  clearCache,
  
  // Force refresh user stats (bypass cache)
  async refreshUserStats(): Promise<{ user: any; rankName: string; nextRank: any }> {
    clearCache('user-stats');
    return this.getUserStats();
  }
};

export const getOnlineUsers = async (): Promise<{
  success: boolean;
  onlineUsers: Array<{
    _id: string;
    username: string;
    rankId: number;
    location: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>;
  totalOnline: number;
  lastUpdated: string;
}> => {
  try {
    const response = await monitorApiCall(
      () => api.get('/game/online-users'),
      '/game/online-users'
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching online users:', error);
    throw error;
  }
};
